/**
 * @file mlrClaimantRename.ts
 * @brief Replace a claimant display name inside MLR draft narratives.
 *
 * @details
 * Mirrors BACKEND `claimantName.rewrite.mjs` so §1 name edits update
 * statements and supplements in the editor, not only the identity cell.
 * Generated copy uses Title + Surname ("Mr Aleyada"); that short form is
 * inferred from the text when Case Hub already holds the new name
 * (09-09-2026).
 *
 * @references
 * - BACKEND/src/features/medical-assessment/claimantName.rewrite.mjs
 *
 * @author DES
 * @date 08-09-2026
 */

const TITLES = new Set(["mr", "mrs", "ms", "miss", "dr", "mx", "prof"]);

const TITLE_NAME_RE =
  /\b(?:Mr|Mrs|Ms|Miss|Dr|Mx|Prof)\.?\s+[A-Z][A-Za-z']+(?:\s+[A-Z][A-Za-z']+){0,2}\b/g;

function formatTitle(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower === "dr") return "Dr";
  if (lower === "mx") return "Mx";
  if (lower === "prof") return "Prof";
  if (lower === "mrs") return "Mrs";
  if (lower === "ms") return "Ms";
  if (lower === "miss") return "Miss";
  return "Mr";
}

export function parseClaimantDisplayName(raw: string): {
  title: string;
  forenames: string;
  surname: string;
} {
  const parts = String(raw || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return { title: "", forenames: "", surname: "" };
  }

  const first = parts[0].replace(/\.$/, "");
  const hasTitle = TITLES.has(first.toLowerCase());
  const title = hasTitle ? formatTitle(first) : "";
  const nameParts = hasTitle ? parts.slice(1) : parts;

  if (nameParts.length === 0) {
    return { title, forenames: "", surname: "" };
  }
  if (nameParts.length === 1) {
    return { title, forenames: "", surname: nameParts[0] };
  }
  return {
    title,
    forenames: nameParts.slice(0, -1).join(" "),
    surname: nameParts[nameParts.length - 1],
  };
}

function replaceAll(haystack: string, from: string, to: string): string {
  if (!from || from === to) return haystack;
  return haystack.split(from).join(to);
}

function replaceWord(haystack: string, from: string, to: string): string {
  if (!from || from === to) return haystack;
  const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return haystack.replace(new RegExp(`\\b${escaped}\\b`, "g"), to);
}

export function uniqueClaimantNames(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const next = String(raw || "").trim();
    if (!next) continue;
    const key = next.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(next);
  }
  return out.sort((a, b) => b.length - a.length);
}

export function inferStaleClaimantNames(
  text: string,
  currentName: string,
): string[] {
  const current = parseClaimantDisplayName(currentName);
  const currentSurname = String(current.surname || "").toLowerCase();
  const currentFull = String(currentName || "").trim().toLowerCase();
  const currentShort = [current.title, current.surname]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const plain = String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ");

  const bySurname = new Map<
    string,
    { count: number; forms: Map<string, number> }
  >();
  for (const raw of plain.match(TITLE_NAME_RE) ?? []) {
    const normalized = raw.replace(/\./g, "").replace(/\s+/g, " ").trim();
    const parsed = parseClaimantDisplayName(normalized);
    if (!parsed.surname || parsed.surname.length < 3) continue;
    const lower = normalized.toLowerCase();
    if (lower === currentFull || lower === currentShort) continue;
    if (currentSurname && parsed.surname.toLowerCase() === currentSurname) {
      continue;
    }
    const surnameKey = parsed.surname.toLowerCase();
    let bucket = bySurname.get(surnameKey);
    if (!bucket) {
      bucket = { count: 0, forms: new Map<string, number>() };
      bySurname.set(surnameKey, bucket);
    }
    bucket.count += 1;
    bucket.forms.set(normalized, (bucket.forms.get(normalized) || 0) + 1);
  }

  let best: { count: number; forms: Map<string, number> } | null = null;
  for (const bucket of bySurname.values()) {
    if (!best || bucket.count > best.count) best = bucket;
  }
  if (!best) return [];
  return [...best.forms.keys()].sort((a, b) => b.length - a.length);
}

export function rewriteClaimantMentions(
  text: string,
  fromName: string,
  toName: string,
): string {
  const source = String(text ?? "");
  const from = String(fromName || "").trim();
  const to = String(toName || "").trim();
  if (!source || !from || !to || from === to) return source;

  const fromParts = parseClaimantDisplayName(from);
  const toParts = parseClaimantDisplayName(to);
  let next = replaceAll(source, from, to);

  const fromShort = [fromParts.title, fromParts.surname]
    .filter(Boolean)
    .join(" ");
  if (fromShort && fromShort !== from && fromShort !== to) {
    next = replaceAll(next, fromShort, to);
  }

  if (
    fromParts.surname &&
    toParts.surname &&
    fromParts.surname !== toParts.surname &&
    fromParts.surname.length >= 3
  ) {
    next = replaceWord(next, fromParts.surname, toParts.surname);
  }

  return next;
}

export function rewriteClaimantMentionsMany(
  text: string,
  fromNames: string[],
  toName: string,
): string {
  const to = String(toName || "").trim();
  if (!to) return String(text ?? "");
  let next = String(text ?? "");
  for (const from of uniqueClaimantNames(fromNames)) {
    if (from.toLowerCase() === to.toLowerCase()) continue;
    next = rewriteClaimantMentions(next, from, to);
  }
  return next;
}

export function rewriteStringMap(
  map: Record<string, string>,
  fromName: string,
  toName: string,
): Record<string, string> {
  return rewriteStringMapMany(map, [fromName], toName);
}

export function rewriteStringMapMany(
  map: Record<string, string>,
  fromNames: string[],
  toName: string,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(map)) {
    next[key] = rewriteClaimantMentionsMany(value, fromNames, toName);
  }
  return next;
}
